import { faCaretUp } from '@fortawesome/pro-solid-svg-icons';
import {
  type FC, useCallback, useMemo,
} from 'react';
import {
  RoundButtonComponent, RoundButtonSize,
} from '~/_shared/baseComponents/buttons';
import { CheckboxComponent } from '~/_shared/baseComponents/checkbox';
import { FontAwesomeIcon } from '~/_shared/baseComponents/icon/fontAwesomeIcon.component';
import { LinkComponent } from '~/_shared/baseComponents/links';
import { TooltipComponent } from '~/_shared/baseComponents/tooltip/tooltip.component';
import { useTheme } from '~/_shared/themes/theme.hooks';
import type {
  LayeringType, MapPrivacyLevel, MapSource,
} from '~/_shared/types/map';
import { formatDate } from '~/_shared/utils/date/date.helpers';
import { useTranslation } from '~/_shared/utils/hooks';
import { ignoreInScreenshotsDataAttribute } from '~/_shared/utils/testing/utils';
import {
  getMapRowFlags, getSubMapsCount,
} from '~/map/listing/mapListing.helpers';
import {
  getMapRowPrivacyIcon, mapListingTableLastStyle, mapListingTablePrimaryStyle, mapListingTableQuaternaryStyle,
  mapListingTableQuinaryStyle,
} from '~/map/listing/mapListing.styles';
import { getMapRowIcon } from '~/map/listing/tableData/mapRow/mapRow.helpers';
import {
  checkboxWrapperStyles, linkIconStyle, mapRowLinkStyle, mapRowNameStyle, mapRowValueStyle, mapSelectCheckboxStyles,
  snapshotsUncollapseArrowStyle, subItemTableDataStyle, subMapsCountContainerStyle, subMapsCountStyle, tableDataStyle,
  tableRowStyle, toggleIconStyle, toggleStyle,
} from '~/map/listing/tableData/mapRow/mapRow.styles';
import { MapRowActionsContainer } from '~/map/listing/tableData/mapRow/mapRowActions.container';
import {
  compareIdToParentMapOrMapId, getMapUrl,
} from '~/map/map.helpers';
import { useMapInfoDataSelector } from '~/store/mapInfo/mapInfo.selectors';
import { type MapListingItem } from '../../item/mapListingItem';
import { type ChangePrivacyLevelProps } from '../../useMapListingItems';
import { MapRowToggleComponent } from './mapRowToggle.component';

export type MapPrivacyClickParams = {
  isBaseLayer: boolean;
  mapId: number;
  mapName: string;
  mapSource: MapSource;
  parentMapId?: number;
  parentMapPrivacy?: MapPrivacyLevel;
  privacyLevel: MapPrivacyLevel;
};

export type MapListingRowItem = Readonly<{
  dateCreated: Date;
  dateUpdated?: Date;
  id: number;
  isBaseLayer?: boolean;
  isLayered: boolean;
  isReadOnly?: boolean;
  layering?: LayeringType;
  mapViews: number;
  name: string;
  noAccess?: boolean;
  privacy?: MapPrivacyLevel;
  shareId?: string;
  sharedUsers: ReadonlyArray<{ id: number; name: string; accessId: number }>;
  snapshots: ReadonlyArray<MapListingItem>;
  source: MapSource;
}>;

type MapRowProps = Readonly<{
  areSubMapsExpanded: boolean;
  index: number;
  isMapSelected: boolean;
  isOwner: boolean;
  item: MapListingRowItem;
  parentMapItem?: MapListingItem;

  onMapPrivacyClick: (data: MapPrivacyClickParams) => void;
  onMapSelectToggle: (mapId: number) => void;
  onShareSuccess: (props: ChangePrivacyLevelProps) => void;
  onShowSubMapsToggle: (mapId: number) => void;
  width: number;
}>;

export const MapRowComponent: FC<MapRowProps> = ({
  item,
  parentMapItem,
  ...props
}) => {
  const [t] = useTranslation();
  const theme = useTheme();
  const mapInfo = useMapInfoDataSelector();

  const subMapsCount = getSubMapsCount(item);
  const privacy = item.privacy;

  const { isBaseLayer, isSnapshot, isSubItem, canMapBeShared, userHasAccess } = getMapRowFlags(item, parentMapItem);

  const checkboxTooltip = useMemo(() => {
    if (!props.isOwner) {
      return isSnapshot
        ? props.isMapSelected
          ? t('Deleting a map also deletes all its map views, including map views of other owners')
          : t('Only the owner is allowed to delete their map views.')
        : t('Only the owner is allowed to delete their maps.');
    }
    return '';
  }, [isSnapshot, props.isMapSelected, props.isOwner, t]);

  const onMapPrivacyClick = useCallback(() => {
    if (!privacy) {
      return;
    }

    const onClick = props.onMapPrivacyClick;
    onClick({
      isBaseLayer,
      mapId: item.id,
      mapName: item.name,
      mapSource: item.source,
      parentMapId: parentMapItem?.id,
      parentMapPrivacy: parentMapItem?.privacy,
      privacyLevel: privacy,
    });
  }, [privacy, props.onMapPrivacyClick, isBaseLayer, item.id, item.name, item.source, parentMapItem?.id,
    parentMapItem?.privacy]);

  return (
    <tr css={tableRowStyle({ theme, isSubItem })} data-testid={props.isMapSelected ? 'selected-map-row' : undefined}>
      <td css={isSubItem ? subItemTableDataStyle : tableDataStyle}>
        <div css={mapRowNameStyle({ isSubItem, rowWidth: props.width })}>
          <span css={checkboxWrapperStyles(isBaseLayer)}>
            <TooltipComponent tooltipContent={checkboxTooltip}>
              <CheckboxComponent
                checkedSetter={() => props.onMapSelectToggle(item.id)}
                isChecked={props.isMapSelected}
                isDisabled={!props.isOwner || isBaseLayer}
                checkmarkStyle={mapSelectCheckboxStyles(props.isMapSelected)}
              />
            </TooltipComponent>
          </span>
          <LinkComponent
            css={mapRowLinkStyle({ theme, isActive: compareIdToParentMapOrMapId(item.id, mapInfo) })}
            data-testid={`navigate-map-${item.name}`}
            disabled={!userHasAccess}
            path={userHasAccess ? getMapUrl(item.id) : null}
            title={item.name}
            tooltip={userHasAccess ? undefined : t('map.noAccess')}
          >
            <FontAwesomeIcon
              css={linkIconStyle}
              icon={getMapRowIcon({
                isSnapshot,
                item,
              })}
            />
            {item.name}
          </LinkComponent>
        </div>
      </td>

      <td css={[tableDataStyle, subMapsCountContainerStyle, mapListingTablePrimaryStyle(props.width)]}>
        {!isSubItem && (
          <MapRowToggleComponent
            areSubMapsExpanded={props.areSubMapsExpanded}
            item={item}
            onClick={props.onShowSubMapsToggle}
            css={subMapsCountContainerStyle}
          >
            <span css={subMapsCountStyle({ theme })}>
              {subMapsCount}
            </span>
          </MapRowToggleComponent>
        )}
      </td>

      <td css={[tableDataStyle, mapListingTableQuinaryStyle(props.width)]}>
        <MapRowToggleComponent
          areSubMapsExpanded={props.areSubMapsExpanded}
          item={item}
          onClick={props.onShowSubMapsToggle}
        >
          <span css={mapRowValueStyle}>
            {formatDate(item.dateCreated).toUpperCase()}
          </span>
        </MapRowToggleComponent>
      </td>

      <td css={[tableDataStyle, mapListingTableQuinaryStyle(props.width)]} {...ignoreInScreenshotsDataAttribute}>
        <MapRowToggleComponent
          areSubMapsExpanded={props.areSubMapsExpanded}
          item={item}
          onClick={props.onShowSubMapsToggle}
        >
          <span css={mapRowValueStyle}>
            {item.dateUpdated && formatDate(item.dateUpdated).toUpperCase()}
          </span>
        </MapRowToggleComponent>
      </td>

      <td css={[tableDataStyle, mapListingTableQuaternaryStyle(props.width)]}>
        <span css={mapRowValueStyle}>
          {item.mapViews}
        </span>
      </td>

      <td css={[tableDataStyle, mapListingTablePrimaryStyle(props.width)]}>
        {privacy && userHasAccess && (
          <RoundButtonComponent
            tooltipDelayShow={500}
            tooltipLabel={canMapBeShared ? t('Map Security') : t('readOnly.warning.functionalityNotAvailableGeneric')}
            icon={getMapRowPrivacyIcon(privacy)}
            onClick={canMapBeShared ? onMapPrivacyClick : undefined}
            size={RoundButtonSize.Small}
            isDisabled={!canMapBeShared}
          />
        )}
      </td>

      <td css={[tableDataStyle, mapListingTableLastStyle(props.width)]}>
        {userHasAccess && (
          <MapRowActionsContainer
            width={props.width}
            isOwner={props.isOwner}
            mapListingItem={item}
            parentMapItem={parentMapItem}
            onShareSuccess={props.onShareSuccess}
          />
        )}
      </td>

      <td css={snapshotsUncollapseArrowStyle}>
        {subMapsCount > 0 && (
          <MapRowToggleComponent
            areSubMapsExpanded={props.areSubMapsExpanded}
            item={item}
            onClick={props.onShowSubMapsToggle}
          >
            <span css={toggleStyle({ theme })}>
              <FontAwesomeIcon
                css={toggleIconStyle({ isToggled: props.areSubMapsExpanded })}
                icon={faCaretUp}
              />
            </span>
          </MapRowToggleComponent>
        )}
      </td>
    </tr>
  );
};
